// Counter — board `ColumnFurniture` `.count`, verbatim values (mono 11 / .18em, the arrows in the
// display face at 15, muted; the current number tungsten, the total dim).
//
// `layout="row"` second dev pass (dev fix 2026-09-18, `Counter.tsx`'s own header doc,
// `thinking/threads/general_generator.md`): the row used to be one inline group — arrows, readout,
// pin door, all in a line — that re-centred under the thumb whenever the pin door came and went (a
// done take carries one, the basis and a queued/rendering take never did). A fixed TRAVEL group
// (`.travel`, centred in `.count`'s own box) and a STANDING pin slot (`.pinSlot`, absolutely at the
// row's own right end) replace it: neither the travel group's position nor its own width depends on
// whether a pin stands beside it, live or idle.
.count {
    position: relative;
    width: 100%;
    min-height: 44px;
    display: flex;
    align-items: center;
}

// The travel group — `‹ readout ›`, centred in `.count`'s own box by absolute positioning + a
// translate, not a flex `justify-content: center` on `.count` itself: a flex-centred group would
// drift toward whichever side has less content the moment the pin slot claims real flex space, which
// is the exact defect this pass fixes.
.travel {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    gap: 8px;
}

// The readout — `9 / 12`, mono, tracked; a fixed minimum column width so a digit-count change
// (`9 / 12` -> `10 / 12`, or `9/9` -> `10/9`) never shifts the arrows flanking it — tabular-nums alone
// only holds each digit's own width steady, not the string's total length.
.readout {
    min-width: 5ch;
    text-align: center;
    font-variant-numeric: tabular-nums;
    font-family: var(--tl-font-label);
    font-size: 11px;
    letter-spacing: 0.18em;
}

// ‹ › are real buttons, 44x44 flex-centred (item 7, wall plan d carried items) — the row's own
// visible height may now grow to fit them (this pass: the old padding/negative-margin trick existed
// only to keep an INLINE sibling's line height from growing, which no longer applies once the row
// stands on its own fixed structure rather than sharing a line with the readout and the pin door).
.arw {
    appearance: none;
    background: none;
    border: none;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: var(--tl-font);
    font-size: 20px;
    line-height: 1;
    letter-spacing: 0;
    color: var(--tl-muted);
    cursor: pointer;

    @media (hover: hover) {
        &:hover:not([aria-disabled='true']) {
            color: var(--tl-text);
        }
    }

    // An end of the chain (dev fix 2026-09-18, same rule `.arrowLeft`/`.arrowRight` below carry):
    // `aria-disabled`, never `disabled` — see their own comment for the full mechanism.
    &[aria-disabled='true'] {
        opacity: 0.35;
        cursor: default;
    }

    &:focus-visible {
        outline: 1px solid var(--tl-accent);
        outline-offset: 2px;
        border-radius: var(--tl-radius-art-md);
    }
}

.now {
    color: var(--tl-accent);
}

// Queued or rendering: no picture yet. The dots said so by going dashed in the accent; the number
// says it unlit over the same dash.
.nowWorking {
    color: var(--tl-muted);
    border-bottom: 1px dashed var(--tl-accent);
}

.of {
    color: var(--tl-dim);
}

// The pin slot — OUTSIDE the travel group, standing at the row's own right end regardless of the
// travel group's own width (dev fix 2026-09-18, this file's own header): its presence, absence or
// state (live/idle) never moves `.travel`.
.pinSlot {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
}

// The pin door (board `PinDoor` option 3) — the counter's own muted ink at rest, filled tungsten
// when pinned; never a third colour. Padding/margin follow `.arw`'s own pre-this-pass pattern: a
// generous hit area (44x44 net, item 7's own rule) that does not grow the SPLIT PILL's own visible
// height, pulled left by a small margin so it reads as "after the readout," not a separate control
// bolted on. `layout='row'`'s own standing pin slot uses `.doorRow` instead (below) — its box no
// longer needs to hide inside a shared inline line-height, so it is a plain 44x44 flex box.
.door {
    appearance: none;
    background: none;
    border: none;
    display: inline-flex;
    align-items: center;
    padding: 16px;
    margin: -16px -16px -16px 2px;
    color: var(--tl-muted);
    cursor: pointer;

    svg {
        display: block;
    }

    &:focus-visible {
        outline: 1px solid var(--tl-accent);
        outline-offset: 2px;
        border-radius: var(--tl-radius-art-md);
    }
}

.doorOn {
    color: var(--tl-accent);
}

// `layout='row'`'s own pin door (dev fix 2026-09-18) — `.door`'s padding/negative-margin trick above
// exists only to keep an inline sibling's line height from growing (the split pill's own case); the
// standing pin slot shares no line with anything, so this resets that trick to a plain, symmetric
// 44x44 flex box instead.
.doorRow {
    padding: 0;
    margin: 0;
    width: 44px;
    height: 44px;
    justify-content: center;
}

// The idle pin door (dev fix 2026-09-18, `Counter.tsx`'s own `PinSlotState`): a step that cannot be
// pinned (the basis, or a queued/rendering take — `pinnedAt` is a stamp on a generation row, and
// neither one carries one) still stands the bookmark's outline, at a low opacity, rather than
// vanishing — so the split pill's own width holds constant across every step of a chain, and the row
// layout's pin slot never appears or disappears under a moving thumb.
.doorIdle {
    opacity: 0.35;
    cursor: default;
}

// The counter's own surface — the pill's, and the disc each arrow stands on. An arrow with no ground
// of its own borrowed its contrast from whatever lay behind it (a dimmed wall tile on the column, the
// picture itself on the sheet) and vanished whenever the tones matched — dev look, 2026-09-18.
@mixin counter-surface {
    background: color-mix(in srgb, var(--tl-surface) 92%, transparent);
    border: 1px solid var(--tl-border-strong);
    border-radius: var(--tl-radius-pill);
}

// `layout="split"` (dev fix 2026-09-18, this file's own header) — the arrows float free of the bar,
// flanking `.frameWrap` (`Column.tsx`) rather than sitting inside a shared row, so each gets its own
// hit area outright instead of the `.arw` padding/negative-margin trick (which exists only to keep an
// INLINE sibling's line height from growing — nothing here shares a line with them).
//
// Second dev pass, same day (operator-tested on dev, Safari macOS and a phone,
// `thinking/threads/general_generator.md`): a 44x44 arrow floating 14px off the frame left a
// scrim-sized gap between the two — a near miss on a rapid ‹ ‹ ‹ run fell through to the scrim beneath
// (`Column.tsx`'s own `onClick={onClose}`) and read as the column closing. Each arrow is now a FLANK
// ZONE (`frame.ts`'s `COUNTER_FLANK_W`): a real button spanning the frame's full height, touching it
// directly (`right: 100%` / `left: 100%`, no gap at all) rather than a small island floating beside
// it — there is no seam left for a press to miss into.
//
// A local mixin, not `@extend` — a plain class-to-class `@extend` makes Sass merge the two
// selectors into one comma rule ahead of postcss-modules' own scoping pass, and this file has
// already paid once (see `.pill`'s own comment) for staying clear of cross-declaration tooling
// hazards; `@include` inlines the declarations instead, so each classname compiles as its own
// ordinary rule.
@mixin flank-zone {
    appearance: none;
    background: none;
    border: none;
    position: absolute;
    top: 0;
    bottom: 0;
    width: var(--wall-flank-w);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: var(--tl-font);
    font-size: 20px;
    line-height: 1;
    color: var(--tl-text);
    cursor: pointer;
    // `isolation` keeps the disc (`z-index: -1`) behind the glyph but in front of everything the
    // zone itself stands on.
    isolation: isolate;

    &::before {
        content: '';
        position: absolute;
        left: 50%;
        top: 50%;
        width: 32px;
        height: 32px;
        transform: translate(-50%, -50%);
        z-index: -1;
        @include counter-surface;
    }

    @media (hover: hover) {
        &:hover:not([aria-disabled='true'])::before {
            border-color: var(--tl-accent);
        }
    }

    // The chain's own ends (dev fix 2026-09-18): `aria-disabled`, never `disabled` — a `disabled`
    // button swallows its own click rather than the wall's scrim beneath it, so the LAST press of a
    // rapid run at an end of the chain used to land on nothing at all and read as the column closing.
    // `aria-disabled` keeps the zone a real click target that absorbs the press and does nothing (the
    // guard lives in the handler, `Counter.tsx`).
    &[aria-disabled='true'] {
        opacity: 0.35;
        cursor: default;
    }

    // Inset, not the usual positive offset (`.arw`'s own `outline-offset: 2px`): the zone sits flush
    // against the column's own stage, and a positive offset here would draw half the ring outside the
    // zone's own box, where the stage's `overflow` (or, on the left zone against a narrow viewport,
    // the column's own edge) could clip it.
    &:focus-visible {
        outline: 1px solid var(--tl-accent);
        outline-offset: -2px;
        border-radius: var(--tl-radius-art-md);
        color: var(--tl-text);
    }
}

.arrowLeft {
    @include flank-zone;
    right: 100%;
}

.arrowRight {
    @include flank-zone;
    left: 100%;
}

// `layout="inside"` (`Counter.tsx`'s own header doc, 2026-09-18) — the same flank zone, drawn over
// the picture's own edges rather than beside it: no room stands beside the picture on the phone
// sheet. `width: 56px` is a literal, not `--wall-flank-w` (`frame.ts`'s `COUNTER_FLANK_W`, the
// column's own 72px) — nothing in `frame.ts` computes a room budget around this one, since the zones
// sit INSIDE the frame's own box rather than adding to what it needs beside it. The glyph stands on
// the mixin's own disc, so a photograph behind it changes nothing.
.zoneLeft,
.zoneRight {
    @include flank-zone;
    width: 56px;
    // See `.pill`'s own comment below — `layout='inside'` renders these as children of `.result`,
    // in direct z-index competition with `WorkingCell`'s `.numeral` (z-index: 2) while a take is
    // queued or rendering.
    z-index: 3;
}

.zoneLeft {
    left: 0;
    right: auto;
}

.zoneRight {
    right: 0;
    left: auto;
}

// The readout and the pin door, together, inside the frame's own bottom-right corner — the surface
// recipe is `.esc`'s own (`Column.module.scss`), restated here rather than shared across files: the
// declarations are three lines, and a cross-file `composes`/`@import` for three lines is the exact
// hazard commit `951cbbf95` paid down (a raw cross-file load, or a second Sass entry point, for
// something this small is not worth carrying).
//
// `z-index: 3` — two placements share this class. `layout='split'` (`Column.tsx`) renders the pill as
// a SIBLING of `.result` inside `.frameWrap`; there `.result`'s own `container-type: inline-size`
// (`Column.module.scss`) already gives it a stacking context of its own, so anything the pill needs
// to clear (the picture, `WorkingCell`) is sealed inside that box regardless of this value — any
// positive number would do. `layout='inside'` (`Sheet.tsx`) renders the pill as a CHILD of `.result`
// instead (no room stands beside the picture on the phone), which puts it in direct z-index
// competition with `WorkingCell`'s own `.numeral` (`WorkingCell.module.scss`, `z-index: 2`) inside
// that same box — `3` is what actually clears it there. `.zoneLeft`/`.zoneRight` (above) carry the
// same value for the identical reason.
.pill {
    position: absolute;
    right: 8px;
    bottom: 8px;
    z-index: 3;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    padding: 4px 10px;
    @include counter-surface;
    font-family: var(--tl-font-label);
    font-size: 11px;
    letter-spacing: 0.18em;
}

// `layout='inside'` (the phone sheet): centred on the picture's bottom edge rather than in its
// corner — the corner lies under `.zoneRight`, which spans the picture's full height and, coming
// later in the DOM at the same z-index, took every tap meant for the pin door (operator-observed on
// a phone, 2026-09-19). Centred, the pill stands between the two zones; `z-index: 4` keeps it on
// top where a narrow frame (a tall ratio on a small phone) still brings the zones under its ends.
.pillInside {
    z-index: 4;
    right: auto;
    left: 50%;
    transform: translateX(-50%);
}

